/** Every town, every hero, the daily income, and what the kingdom's armies add
 * up to.
 *
 * Driven off `PlayerObservation`, so it shows what the viewer knows and nothing
 * else: an enemy town the fog only remembers contributes a marker and no
 * numbers. Income is resolved through `resolveStat`, the same path the kernel
 * takes, because the hall chain uses `max` and a screen that summed would
 * report a Capitol town at 3500 gold instead of 4000.
 */

import { useMemo } from "react";
import { usePhoneSheetFit } from "../theme/phone.js";
import type { ContentRegistry, HeroState, PlayerObservation, TownState } from "@game/core";
import { PRIMARY_ICON, PRIMARY_LABEL, PRIMARY_ORDER, RESOURCE_ICON, portraitKey } from "../theme/art.js";
import { creatureIcon } from "./art.js";
import { BitmapText } from "../theme/bitmapText.js";
import { compactCount, groupDigits, titleCase } from "./format.js";
import { ICON_SMALL, PORTRAIT_SMALL, ResourceStrip } from "./parts.js";
import { usePolishStyles } from "./polish.js";
import { MaybeIcon, ScreenStyles } from "./primitives.js";
import { summariseKingdom } from "./kingdomModel.js";
import { guildLevelOf, townIncome } from "./townModel.js";

export interface KingdomOverviewProps {
  readonly content: ContentRegistry;
  readonly obs: PlayerObservation;
  readonly onOpenTown?: (town: TownState) => void;
  readonly onOpenHero?: (hero: HeroState) => void;
  readonly onClose: () => void;
}

export function KingdomOverview(props: KingdomOverviewProps): JSX.Element {
  usePolishStyles();
  const fit = usePhoneSheetFit();
  const { content, obs } = props;
  const summary = useMemo(() => summariseKingdom(content, obs), [content, obs]);

  return (
    <div className={`hs-screen ${fit.screenClass}`.trim()} ref={fit.screenRef}
      style={fit.style} role="region" aria-label="Kingdom overview">
      <ScreenStyles />

      <div className="hs-phone-sheet" ref={fit.sheetRef}>
      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title">Kingdom Overview</span>
        <span className="hs-screen__sub">
          {`Day ${String(obs.turn.day)}, week ${String(obs.turn.week)}, month ${String(obs.turn.month)}`}
        </span>
        <span className="hs-spacer" />
        <ResourceStrip resources={summary.treasury} income={summary.income} />
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      <div className="hs-screen__body">
        <div className="hs-kingdom">
          <div className="hs-hero__col">
            <div className="hs-card hs-wood" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div className="hs-card__title">{`Towns (${String(summary.towns.length)})`}</div>
              <div className="hs-list">
                {summary.towns.length === 0 ? (
                  <div className="hs-tiny hs-muted">No towns. Take one before the week is out.</div>
                ) : summary.towns.map((t) => {
                  const income = townIncome(content, t);
                  const guild = guildLevelOf(content, t);
                  return (
                    <button
                      key={t.id as string}
                      type="button"
                      className="hs-listrow"
                      onClick={() => props.onOpenTown?.(t)}
                      title={`${t.name} — ${titleCase(t.faction as string)}`}
                    >
                      <span className="hs-col" style={{ flex: "1 1 auto" }}>
                        <span className="hs-listrow__name hs-clip">{t.name}</span>
                        <span className="hs-listrow__meta hs-clip">
                          {`${titleCase(t.faction as string)} · ${String(t.built.length)} buildings`}
                          {guild > 0 ? ` · mage guild ${String(guild)}` : ""}
                        </span>
                      </span>
                      <span className="hs-cost__item hs-cost__item--ok">
                        <MaybeIcon atlasKey={RESOURCE_ICON.gold} size={ICON_SMALL} title="gold" />
                        {groupDigits(income.gold)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="hs-card hs-wood" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div className="hs-card__title">{`Heroes (${String(summary.heroes.length)})`}</div>
              <div className="hs-list">
                {summary.heroes.length === 0 ? (
                  <div className="hs-tiny hs-muted">No heroes in the field.</div>
                ) : summary.heroes.map((h) => {
                  const def = content.heroes.get(h.def);
                  const army = obs.armies[h.army as string] ?? null;
                  const stacks = army === null ? 0 : army.slots.filter((s) => s !== null).length;
                  return (
                    <button
                      key={h.id as string}
                      type="button"
                      className="hs-listrow"
                      onClick={() => props.onOpenHero?.(h)}
                      title={def?.name ?? (h.def as string)}
                    >
                      <MaybeIcon
                        atlasKey={def === undefined ? null : portraitKey(def.portrait)}
                        size={PORTRAIT_SMALL}
                        title={def?.name ?? "Hero"}
                      />
                      <span className="hs-col" style={{ flex: "1 1 auto" }}>
                        <span className="hs-listrow__name hs-clip">
                          {def?.name ?? titleCase(h.def as string)}
                        </span>
                        <span className="hs-listrow__meta hs-clip">
                          {`${def?.heroClass ?? ""} · level ${String(h.level)} · ${String(stacks)} stacks`}
                        </span>
                      </span>
                      <span className="hs-row">
                        {PRIMARY_ORDER.map((p) => (
                          <span className="hs-cost__item hs-cost__item--ok" key={p}
                            title={PRIMARY_LABEL[p]}>
                            <MaybeIcon atlasKey={PRIMARY_ICON[p]} size={ICON_SMALL} title={PRIMARY_LABEL[p]} />
                            {h.primary[p]}
                          </span>
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="hs-hero__col">
            <div className="hs-card hs-wood">
              <div className="hs-card__title">Daily income</div>
              <ResourceStrip resources={summary.income} />
              <div className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>
                {`${String(summary.towns.length)} towns · best mage guild ${String(summary.bestGuildLevel)}`}
              </div>
            </div>

            <div className="hs-card hs-wood" style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
              <div className="hs-card__title">
                {`Creatures (${groupDigits(summary.totalCreatures)})`}
              </div>
              <div className="hs-list">
                {summary.creatures.length === 0 ? (
                  <div className="hs-tiny hs-muted">Not a single soldier anywhere.</div>
                ) : summary.creatures.map((c) => (
                  <div className="hs-listrow" key={c.def.id as string}
                    title={`${c.def.namePlural}: ${groupDigits(c.inArmies)} with heroes, ${groupDigits(c.inTowns)} in garrisons`}>
                    <MaybeIcon atlasKey={creatureIcon(c.def)} size={40} title={c.def.name} />
                    <span className="hs-col" style={{ flex: "1 1 auto" }}>
                      <span className="hs-listrow__name hs-clip">{c.def.namePlural}</span>
                      <span className="hs-listrow__meta">
                        {`tier ${String(c.def.tier)}${c.def.upgraded ? "+" : ""}`}
                      </span>
                    </span>
                    <span className="hs-num">
                      <BitmapText scale={1}>{compactCount(c.count)}</BitmapText>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
